import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Badge } from "../../../../components/ui/badge";
import { Check, X, Pencil } from "lucide-react";

interface InlineEditableMultiSelectOption {
  value: string;
  label: string;
}

interface InlineEditableMultiSelectProps {
  values: string[];
  options: InlineEditableMultiSelectOption[];
  onSave: (newValues: string[]) => Promise<void>;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
}

export const InlineEditableMultiSelect = ({
  values,
  options,
  onSave,
  disabled = false,
  className = "",
  emptyMessage = "No items selected",
}: InlineEditableMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(values);
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (value: string) => {
    setSelectedValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSave = async () => {
    if (
      JSON.stringify([...selectedValues].sort()) ===
      JSON.stringify([...values].sort())
    ) {
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedValues);
      setIsOpen(false);
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedValues(values);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSelectedValues(values);
    }
    setIsOpen(open);
  };

  const selectedLabels = values
    .map((val) => options.find((opt) => opt.value === val)?.label || val)
    .filter(Boolean);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className={`group cursor-pointer rounded p-2  transition-colors ${className} ${
            disabled ? "cursor-default" : ""
          }`}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              {selectedLabels.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedLabels.map((label, i) => (
                    <Badge key={i} variant="outline" className="bg-stone-50">
                      {label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-font-secondary italic">
                  {emptyMessage}
                </span>
              )}
            </div>
            {!disabled && (
              <Pencil className="h-3 w-3 text-font-secondary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" sideOffset={8}>
        <div className="flex flex-col max-h-[300px]">
          <div className="overflow-y-auto p-4 space-y-2">
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-start gap-3 py-2 px-2 rounded hover:bg-stone-50 cursor-pointer"
                onClick={() => handleToggle(option.value)}
              >
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={() => handleToggle(option.value)}
                  className="mt-0.5"
                />
                <label className="flex-1 text-sm cursor-pointer">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          <div className="flex gap-2 p-4 border-t bg-white">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1"
            >
              <Check className="h-3 w-3" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
